/**
 * The pickup half of a delivery address: searching for a station, or showing the chosen one.
 *
 * Markup contract: one scope around both halves of form/fieldset/user_pickup.html.twig. A page
 * carries one fieldset per delivery address, so every id ends in the address id the scope is given.
 *   <div x-data="pickupAddress('<addressId>')" @packstation-picked.window="receive($event.detail)">
 *     <ul id="packstationSearchOptions_<addressId>">… <button @click="validate(searchBox) && search()">
 *     <ul id="chosenPackstationDetail_<addressId>">… <a id="pickOtherPackstation_<addressId>" @click="searchAgain()">
 *
 * The station list opens as a dialog outside this scope, so the pick comes back as a
 * window event and only the fieldset that opened the dialog takes it, all of them listening.
 *
 * The area field guides the search only and leaves the submit while the station is shown;
 * the country select is address data and is always sent. A disabled field reaches neither
 * the server nor the validation, which is how the closed half stays out of both.
 */

// the street token a pick writes; only a DHL station carries a post number
const DHL_STREETS = ['Packstation', 'Postfiliale', 'Parcelshop'];

// at most one dialog is open, so one fieldset waits for a pick; its root marks it
let awaiting = null;

document.addEventListener('alpine:init', () => {
    Alpine.data('pickupAddress', (addressId) => ({
        searching: true,
        postNumber: false,
        // declared so each fieldset keeps its own: Alpine writes an undeclared property to the
        // outermost scope (validatedForm), which every fieldset of the page would share
        root: null,
        searchBox: null,
        detailBox: null,

        init() {
            // $el is the element of the running expression, the button for a @click, so the
            // root is taken here, where the expression is the x-data of the fieldset itself
            this.root = this.$el;
            this.searchBox = this.field('packstationSearchOptions');
            this.detailBox = this.field('chosenPackstationDetail');

            // the street number is what a pick writes, from the server or from local storage
            this.searching = this.field('chosenPackstationStreetNumber').value === '';
            this.postNumber = DHL_STREETS.includes(this.field('chosenPackstationStreetName').value);

            Alpine.effect(() => this.apply());
        },

        field(id) {
            return this.root.querySelector(`[id="${id}_${addressId}"]`);
        },

        /** The summary line of the registration, filled by its confirm button. */
        get summary() {
            return {
                billDelType: this.field('packstationNameText').textContent.trim(),
                billDelStreetName: this.field('packstationStreetText').textContent.trim(),
                billDelZipCode: this.field('chosenPackstationZipCode').value,
                billDelCity: this.field('chosenPackstationCity').value,
            };
        },

        apply() {
            this.searchBox.hidden = !this.searching;
            this.detailBox.style.display = this.searching ? 'none' : 'block';

            this.field('packstationPlz').disabled = !this.searching;
            this.detailBox.querySelectorAll('input, select, textarea').forEach((input) => {
                input.disabled = this.searching;
            });

            this.field('packstationNumberField').hidden = !this.postNumber;
            this.field('chosenPackstationCompany').required = this.postNumber;
        },

        /** Opens the station list of the packstation module for the entered area. */
        search() {
            const query = new URLSearchParams({
                cl: 'packstation',
                land: this.field('packstationLand').value,
                plz: this.field('packstationPlz').value,
            });

            awaiting = this.root;
            Alpine.store('dialog').openFragment(`/${URLSEGMENT}index.php?${query}`);
        },

        searchAgain() {
            this.searching = true;
        },

        receive(address) {
            if (awaiting !== this.root) {
                return;
            }
            awaiting = null;

            this.field('chosenPackstationStreetName').value = address.street;
            this.field('chosenPackstationStreetNumber').value = address.number;
            this.field('chosenPackstationZipCode').value = address.zip;
            this.field('chosenPackstationCity').value = address.city;
            this.field('chosenPackstationAddInfo').value = address.addInfo;
            this.field('chosenPackstationFlag').value = address.flag;
            this.field('packstationFormPlaceholder').querySelectorAll('input').forEach((input) => {
                input.disabled = false;
            });

            this.field('packstationNameText').textContent = address.name;
            this.field('packstationStreetText').textContent = address.addInfo;
            this.field('packstationPLZAndCityText').textContent = `${address.zip} ${address.city}`;

            this.postNumber = address.postNumber;
            this.searching = false;

            this.$nextTick(() => {
                this.field('pickupNotificationAnchor').scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        },
    }));
});
